import { useEffect, useMemo, useRef } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import IconButton from '@savvy-kit/components/icon-button';
import { Spinner } from '@savvy-kit/components/spinner';
import { Text } from '@savvy-kit/foundations/text';
import { MoreHorizontal } from 'lucide-react';
import { graphql } from 'src/gql';
import useIsInViewport from 'src/hooks/use-is-in-viewport';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';
import { assert } from '@savvy/utils/assert';

import NotificationsPeekRow from './notifications-peek-row';

const ACTION_CENTER_PATH = '/advisor/dashboard/co-pilot/v2';
const MANAGE_NOTIFICATIONS_PATH = '/advisor/dashboard/settings?co-pilot';

// Sized to overflow the max-h-[70vh] popover so the first page doesn't immediately
// trip the load-more sentinel; the sentinel self-fills on tall viewports.
const PAGE_SIZE = 15;

// Peek list query — separate from the count query so the bell badge keeps
// loading cheaply and this only fires when the popover is opened.
const notificationsPeekListQuery = graphql(/* GraphQL */ `
  query NotificationsPeekList(
    $userId: ID!
    $alertTypes: [String!]
    $primaryAdvisorIds: [ID!]
    $first: Int
    $after: String
    $groupBulk: Boolean
  ) {
    alerts(
      userId: $userId
      alertTypes: $alertTypes
      primaryAdvisorIds: $primaryAdvisorIds
      first: $first
      after: $after
      groupBulk: $groupBulk
    ) @connection(key: "notificationsPeek", filter: ["alertTypes", "primaryAdvisorIds"]) {
      edges {
        node {
          id
          ...NotificationsPeekRow
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

type Props = {
  readonly alertTypes: readonly string[];
  readonly primaryAdvisorIds: readonly string[];
  /** ISO timestamp of the advisor's last visit to the action center; used to derive unread row state. */
  readonly visitedAt: string | null;
  readonly onClose: () => void;
};

function NotificationsPeekPanel({ alertTypes, primaryAdvisorIds, visitedAt, onClose }: Props) {
  const router = useRouter();
  const { user } = useAuth();
  assert(
    user !== null && user !== undefined && user.id !== null && user.id !== undefined,
    'User and ID should be defined',
  );
  const userId: string = user.id;

  const variables = useMemo(
    () => ({
      userId,
      alertTypes: alertTypes.length > 0 ? [...alertTypes] : undefined,
      primaryAdvisorIds: [...primaryAdvisorIds],
      first: PAGE_SIZE,
      // Collapse bulk clusters into one summary row, matching the Inbox tab (UNO-588).
      groupBulk: true,
    }),
    [userId, alertTypes, primaryAdvisorIds],
  );

  const { data, loading, fetchMore } = useQueryWithErrorLogging(notificationsPeekListQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const nodes = (data?.alerts?.edges ?? []).flatMap((edge) =>
    edge !== null && edge !== undefined && edge.node !== null && edge.node !== undefined ? [edge.node] : [],
  );

  const isLoading = loading && nodes.length === 0;
  const hasItems = nodes.length > 0;

  // alerts and pageInfo are non-null in the schema, so a field error null-bubbles all
  // the way to `data`; `data?.` alone is safe and the extra chains trip no-unnecessary-condition.
  const hasNextPage = data?.alerts.pageInfo.hasNextPage === true;
  const endCursor = data?.alerts.pageInfo.endCursor ?? null;

  // Auto-load the next page when the bottom sentinel scrolls into view. The Apollo
  // `alerts` type policy merges pages by `after` cursor, so no manual cache write is needed.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isSentinelVisible = useIsInViewport(sentinelRef);
  useEffect(() => {
    if (isSentinelVisible && hasNextPage && !loading && endCursor !== null) {
      void fetchMore({ variables: { after: endCursor } });
    }
  }, [isSentinelVisible, hasNextPage, loading, endCursor, fetchMore]);

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="flex items-center justify-center py-10">
        <Spinner className="size-5" />
      </div>
    );
  } else if (hasItems) {
    body = (
      <ul className="space-y-0.5">
        {nodes.map((node) => (
          <NotificationsPeekRow key={node.id} alertFrag={node} visitedAt={visitedAt} onSelect={onClose} />
        ))}
      </ul>
    );
  } else {
    body = (
      <div className="px-3 py-10 text-center">
        <Text className="block text-sm text-secondary">You&apos;re all caught up.</Text>
      </div>
    );
  }

  return (
    <div className="flex max-h-[70vh] w-[380px] flex-col">
      <div className="flex items-center justify-between border-b border-light px-4 py-3">
        <Text className="text-sm font-semibold">Inbox</Text>
        <div className="flex items-center gap-1">
          <NextLink
            href={ACTION_CENTER_PATH}
            onClick={onClose}
            className="text-sm font-medium text-primary hover:underline"
          >
            Show all
          </NextLink>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <IconButton type="button" variant="ghost" size="xs" aria-label="More notification options">
                  <MoreHorizontal className="size-4" />
                </IconButton>
              }
            />
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem
                onClick={() => {
                  router.push(MANAGE_NOTIFICATIONS_PATH);
                  onClose();
                }}
              >
                Change preferences
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {body}
        {hasItems && loading && (
          <div className="flex items-center justify-center py-3">
            <Spinner className="size-4" />
          </div>
        )}
        <div ref={sentinelRef} aria-hidden className="h-px" />
      </div>
    </div>
  );
}

export default NotificationsPeekPanel;
